import { useState, useMemo } from 'react';
import { formatDisplayName } from '../../utils/nameUtils';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Badge, Alert, Modal } from 'flowbite-react';
import { HiPlus, HiMail, HiPhone, HiPencil, HiTrash, HiRefresh, HiExclamation } from 'react-icons/hi';
import { DataTable, type ColumnDef } from '../tables';
import { EmptyState } from '../common/EmptyState';
import { leasApi, staffApi } from '../../api';
import type { LEAResponse, RoleTagSummary } from '../../types/api';
import { CreateStaffModal } from '../modals/CreateStaffModal';
import { ManageContactStatusModal } from '../modals/ManageContactStatusModal';
import { AssignmentsManager } from '../staff/AssignmentsManager';

interface StaffTabProps {
  lea: LEAResponse;
  onUpdate: () => void;
  canEdit?: boolean;
}

interface StaffMember {
  assignmentId: number;
  personId: number;
  staffId?: number;
  fullName: string;
  lastName: string;
  email?: string;
  phone?: string;
  roleDescriptor: string;
  positionTitle: string | null;
  areaOfResponsibility?: string[];
  roleTags: RoleTagSummary[];
  includeInDirectory: boolean;
  contactType?: string;
  organizationName?: string | null;
  endDate?: string | null;
}

// Check if a staff member's assignment has ended
const isContactInactive = (endDate?: string | null): boolean => {
  if (!endDate) return false;
  const end = new Date(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return end <= today;
};

export const StaffTab: React.FC<StaffTabProps> = ({ lea, onUpdate, canEdit = false }) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCreateStaffModal, setShowCreateStaffModal] = useState(false);
  const [showManageAssignmentsModal, setShowManageAssignmentsModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<{ staffId: number; personId: number; personName: string; assignments: any[] } | null>(null);
  const [showManageStatusModal, setShowManageStatusModal] = useState(false);
  const [selectedPersonForStatus, setSelectedPersonForStatus] = useState<{ personId: number; staffId: number; name: string } | null>(null);
  const [showUntaggedOnly, setShowUntaggedOnly] = useState(false);

  // Filter out VOTING board members and solicitors - they go exclusively in the Board tab
  // Non-voting board roles (board-related but no BoardMember SEOAA) appear on BOTH tabs
  const staffMembers: StaffMember[] = useMemo(() => {
    if (!lea.administrators) return [];

    // First, find all people who have a BoardMember role (voting members)
    // These are the only board-related people we exclude from Staff tab
    const votingBoardMemberPersonIds = new Set<number>();
    for (const admin of lea.administrators) {
      const role = admin.role?.toLowerCase() || '';
      if (role === 'boardmember') {
        votingBoardMemberPersonIds.add(admin.personId);
      }
    }

    const members = lea.administrators
      .filter(s => {
        const role = s.role?.toLowerCase() || '';
        const title = s.positionTitle?.toLowerCase() || '';

        // Exclude voting board members (people with BoardMember SEOAA)
        const isVotingBoardMember = votingBoardMemberPersonIds.has(s.personId);

        // Exclude solicitors
        const isSolicitor = role === 'solicitor' || title.includes('solicitor');

        // Also exclude board-specific SEOAA records for non-voting people
        // (they'll show with their primary admin role, not their board tag)
        const isBoardRoleRecord = role.startsWith('board');

        return !isVotingBoardMember && !isSolicitor && !isBoardRoleRecord;
      })
      .map(s => ({
        assignmentId: s.staffEdOrgAssignmentAssociationId,
        personId: s.personId,
        staffId: s.staffId,
        fullName: s.displayName || formatDisplayName(s),
        lastName: s.lastName,
        email: s.email || undefined,
        phone: s.phone || undefined,
        roleDescriptor: s.positionTitle || '',
        positionTitle: s.positionTitle,
        areaOfResponsibility: undefined,
        roleTags: s.roleTags || [],
        includeInDirectory: s.includeInDirectory !== false,
        contactType: s.contactType || 'District Administrator',
        organizationName: s.organizationName,
        endDate: s.endDate
      }));

    // Sort to put School Administrators at the bottom
    return members.sort((a, b) => {
      if (a.contactType === 'School Administrator' && b.contactType !== 'School Administrator') {
        return 1;
      }
      if (a.contactType !== 'School Administrator' && b.contactType === 'School Administrator') {
        return -1;
      }
      // Secondary sort by last name, then full name as tiebreaker
      return a.lastName.localeCompare(b.lastName) || a.fullName.localeCompare(b.fullName);
    });
  }, [lea.administrators]);

  // Current directory contacts with no role tag can't be found with the Role
  // filter in Search Contacts. Editors see how many, and can list just those.
  const untaggedMembers = useMemo(
    () => staffMembers.filter(m =>
      m.roleTags.length === 0 && m.includeInDirectory && !isContactInactive(m.endDate) &&
      !!m.positionTitle?.trim()
    ),
    [staffMembers]
  );
  // Falls back to everyone once the last untagged contact is fixed
  const tableData = canEdit && showUntaggedOnly && untaggedMembers.length > 0 ? untaggedMembers : staffMembers;

  const handleDeleteStaff = async (assignmentId: number, fullName: string) => {
    if (!confirm(`Are you sure you want to remove ${fullName} from this organization?`)) {
      return;
    }

    try {
      setError(null);
      await leasApi.removeStaff(lea.leaId, assignmentId);
      setSuccessMessage(`Successfully removed ${fullName}`);
      onUpdate();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to remove staff member');
    }
  };

  const handleStaffCreated = async (staffId: number, _personId: number) => {
    // Staff created - now fetch full staff record and show assignments manager
    setShowCreateStaffModal(false);

    try {
      const fullStaff = await staffApi.get(staffId);
      setSelectedStaff({
        staffId: fullStaff.staffId,
        personId: fullStaff.person.personId,
        personName: formatDisplayName({ ...fullStaff.person, lastName: fullStaff.person.lastSurname }),
        assignments: fullStaff.assignments
      });
      setShowManageAssignmentsModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load staff member');
    }
  };

  const handleEditAssignments = async (personId: number, _assignmentId: number) => {
    // Find the staff member by matching person ID from the administrators list
    const admin = lea.administrators?.find(a => a.personId === personId);
    if (!admin || !admin.staffId) {
      // Fallback: navigate to person profile
      navigate(`/persons/${personId}`);
      return;
    }

    try {
      const fullStaff = await staffApi.get(admin.staffId);
      setSelectedStaff({
        staffId: fullStaff.staffId,
        personId: fullStaff.person.personId,
        personName: formatDisplayName({ ...fullStaff.person, lastName: fullStaff.person.lastSurname }),
        assignments: fullStaff.assignments
      });
      setShowManageAssignmentsModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load staff member');
    }
  };

  const handleUpdateAssignments = async () => {
    // Refresh the organization data to show updated assignments
    onUpdate();

    // Refresh the selected staff member's assignments
    if (selectedStaff) {
      try {
        const updatedStaff = await staffApi.get(selectedStaff.staffId);
        setSelectedStaff({
          ...selectedStaff,
          assignments: updatedStaff.assignments
        });
      } catch (err) {
        console.error('Failed to refresh staff assignments:', err);
      }
    }
  };

  const columns: ColumnDef<StaffMember>[] = useMemo(() => [
    {
      id: 'fullName',
      header: 'Name',
      accessor: 'fullName',
      sortable: true,
      searchable: true,
      render: (_value, row) => (
        <Link
          to={`/persons/${row.personId}`}
          className="text-left hover:underline block"
        >
          <div className="flex items-center gap-2">
            <span className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">{row.fullName}</span>
            {isContactInactive(row.endDate) && (
              <Badge color="gray" size="xs">Inactive</Badge>
            )}
          </div>
          {!row.includeInDirectory && (
            <span className="text-xs text-orange-600 dark:text-orange-400">Not in directory</span>
          )}
        </Link>
      )
    },
    {
      id: 'contactType',
      header: 'Contact Type',
      accessor: 'contactType',
      sortable: true,
      render: (_value, row) => (
        <div>
          <Badge color={row.contactType === 'School Administrator' ? 'blue' : 'gray'} size="sm">
            {row.contactType}
          </Badge>
          {row.organizationName && row.contactType === 'School Administrator' && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{row.organizationName}</div>
          )}
        </div>
      )
    },
    {
      id: 'role',
      header: 'Role',
      accessor: 'positionTitle',
      sortable: true,
      searchable: true,
      nowrap: false,
      render: (_value, row) => (
        <div>
          <div className="font-medium text-gray-700 dark:text-gray-300">
            {row.positionTitle || row.roleDescriptor}
          </div>
          {row.roleTags.length > 0 ? (
            <div className="flex flex-wrap gap-1 mt-1">
              {row.roleTags.map(tag => (
                <span
                  key={tag.descriptorId}
                  className="inline-block px-2 py-0.5 text-xs rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300"
                >
                  {tag.shortDescription}
                </span>
              ))}
            </div>
          ) : canEdit && row.includeInDirectory && !isContactInactive(row.endDate) && row.positionTitle?.trim() ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEditAssignments(row.personId, row.assignmentId);
              }}
              className="mt-1 inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 hover:underline"
            >
              <HiExclamation className="h-3.5 w-3.5" />
              No role tag
            </button>
          ) : null}
          {row.areaOfResponsibility && row.areaOfResponsibility.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {row.areaOfResponsibility.map((area, index) => (
                <span
                  key={index}
                  className="inline-block px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                >
                  {area}
                </span>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      id: 'contact',
      header: 'Contact',
      accessor: 'email',
      sortable: true,
      searchable: true,
      nowrap: false,
      render: (_value, row) => (
        <div className="space-y-1">
          {row.email ? (
            <a
              href={`mailto:${row.email}`}
              className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              onClick={(e) => e.stopPropagation()}
            >
              <HiMail className="mr-1 h-4 w-4" />
              {row.email}
            </a>
          ) : null}
          {row.phone ? (
            <a
              href={`tel:${row.phone}`}
              className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              onClick={(e) => e.stopPropagation()}
            >
              <HiPhone className="mr-1 h-4 w-4" />
              {row.phone}
            </a>
          ) : null}
          {!row.email && !row.phone && (
            <span className="text-sm text-gray-400 dark:text-gray-500 italic">Not available</span>
          )}
        </div>
      )
    },
    // Only include actions column if user can edit
    ...(canEdit ? [{
      id: 'actions',
      header: 'Actions',
      accessor: 'assignmentId' as keyof StaffMember,
      sortable: false,
      searchable: false,
      width: '100px',
      align: 'center' as const,
      render: (_value: any, row: StaffMember) => (
        <div className="flex justify-center gap-2">
          <Button
            size="xs"
            color="light"
            onClick={(e) => {
              e.stopPropagation();
              handleEditAssignments(row.personId, row.assignmentId);
            }}
            className="text-gray-600 hover:text-gray-700"
            aria-label={`Edit ${row.fullName}`}
          >
            <HiPencil className="h-4 w-4" />
          </Button>
          <Button
            size="xs"
            color="light"
            onClick={(e) => {
              e.stopPropagation();
              if (row.staffId) {
                setSelectedPersonForStatus({
                  personId: row.personId,
                  staffId: row.staffId,
                  name: row.fullName
                });
                setShowManageStatusModal(true);
              } else {
                // Fallback: direct delete if no staffId (shouldn't happen)
                handleDeleteStaff(row.assignmentId, row.fullName);
              }
            }}
            className={isContactInactive(row.endDate)
              ? "text-blue-600 hover:text-blue-700"
              : "text-red-600 hover:text-red-700"}
            aria-label={isContactInactive(row.endDate) ? `Reactivate ${row.fullName}` : `Manage ${row.fullName}`}
          >
            {isContactInactive(row.endDate) ? (
              <HiRefresh className="h-4 w-4" />
            ) : (
              <HiTrash className="h-4 w-4" />
            )}
          </Button>
        </div>
      )
    }] : [])
  ], [handleDeleteStaff, canEdit]);

  if (staffMembers.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          title="No staff members found"
          description="This organization doesn't have any staff members assigned yet."
          action={canEdit ? {
            label: 'Add Staff Member',
            onClick: () => setShowCreateStaffModal(true),
            icon: <HiPlus className="mr-2 h-4 w-4" />
          } : undefined}
        />

        {/* Create Staff Modal */}
        {canEdit && (
          <CreateStaffModal
            isOpen={showCreateStaffModal}
            onClose={() => setShowCreateStaffModal(false)}
            onSuccess={({ staffId, personId }) => handleStaffCreated(staffId, personId)}
            defaultOrganizationId={lea.leaId}
            modalTitle="Add Staff Member"
          />
        )}
      </div>
    );
  }

  return (
    <div className="p-6">
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert color="success" onDismiss={() => setSuccessMessage(null)} className="mb-4">
          {successMessage}
        </Alert>
      )}

      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Administrative Staff
        </h3>
        {canEdit && (
          <Button
            size="sm"
            color="light"
            onClick={() => setShowCreateStaffModal(true)}
            className="text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100"
          >
            <HiPlus className="mr-2 h-4 w-4" />
            Add Staff Member
          </Button>
        )}
      </div>

      {canEdit && untaggedMembers.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
          <span>
            <HiExclamation className="inline h-4 w-4 mr-1 -mt-0.5" />
            {showUntaggedOnly
              ? `Showing ${untaggedMembers.length} contact${untaggedMembers.length === 1 ? '' : 's'} with no role tag.`
              : `${untaggedMembers.length} of ${staffMembers.length} contacts have no role tag, so the Role filter in Search Contacts can't find them.`}
          </span>
          <button
            type="button"
            onClick={() => setShowUntaggedOnly(!showUntaggedOnly)}
            className="font-medium underline hover:no-underline"
          >
            {showUntaggedOnly ? 'Show everyone' : 'Show them'}
          </button>
        </div>
      )}

      {/* Staff Table */}
      <DataTable
        data={tableData}
        columns={columns}
        searchable={true}
        searchPlaceholder="Search staff members..."
        sortable={true}
        pagination={true}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        selectable={false}
        hoverable={true}
        striped={false}
        emptyMessage="No staff members found."
        ariaLabel="Staff members table"
      />

      {/* Create Staff Modal */}
      {canEdit && (
        <CreateStaffModal
          isOpen={showCreateStaffModal}
          onClose={() => setShowCreateStaffModal(false)}
          onSuccess={({ staffId, personId }) => handleStaffCreated(staffId, personId)}
          defaultOrganizationId={lea.educationOrganizationId}
          modalTitle="Add Staff Member"
        />
      )}

      {/* Manage Assignments Modal */}
      {selectedStaff && (
        <Modal
          show={showManageAssignmentsModal}
          onClose={() => {
            setShowManageAssignmentsModal(false);
            setSelectedStaff(null);
          }}
          dismissible
          size="3xl"
        >
          <div className="flex flex-col max-h-[85vh]">
            {/* Header - fixed */}
            <div className="flex items-center justify-between p-6 pb-4 border-b flex-shrink-0">
              <h3 className="text-xl font-medium text-gray-900">
                Manage Assignments: {selectedStaff.personName}
              </h3>
              <button
                onClick={() => {
                  setShowManageAssignmentsModal(false);
                  setSelectedStaff(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Profile link hint */}
            <div className="px-6 pt-3 pb-0">
              <p className="text-sm text-gray-500">
                <a
                  href={`/persons/${selectedStaff.personId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowManageAssignmentsModal(false);
                    setSelectedStaff(null);
                    navigate(`/persons/${selectedStaff.personId}`);
                  }}
                  className="text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Visit {selectedStaff.personName}'s profile
                </a>
                {' '}to edit contact information.
              </p>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-6">
              <AssignmentsManager
                staffId={selectedStaff.staffId}
                assignments={selectedStaff.assignments}
                onUpdate={handleUpdateAssignments}
                lockedOrganizationId={lea.educationOrganizationId}
              />
            </div>

            {/* Footer - fixed */}
            <div className="p-6 pt-4 border-t flex justify-end flex-shrink-0">
              <Button
                color="gray"
                onClick={() => {
                  setShowManageAssignmentsModal(false);
                  setSelectedStaff(null);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Manage Contact Status Modal */}
      {selectedPersonForStatus && (
        <ManageContactStatusModal
          isOpen={showManageStatusModal}
          onClose={() => {
            setShowManageStatusModal(false);
            setSelectedPersonForStatus(null);
          }}
          personId={selectedPersonForStatus.personId}
          personName={selectedPersonForStatus.name}
          staffId={selectedPersonForStatus.staffId}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
};